import { useParams, Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

// Helper function to generate crypto URI for QR code
// Supports EIP-681 format for ERC-20 token transfers
const getCryptoURI = (currency, address, amount, contractAddress = null, chainId = null) => {
  if (!currency || !address) return address || '';
  
  const ticker = currency.toLowerCase();
  
  // ERC-20 tokens that need EIP-681 format for token transfers
  // Format: ethereum:contractAddress@chainId/transfer?address=recipientAddress&uint256=amount
  if (contractAddress && chainId) {
    // Convert amount to smallest unit (wei for ETH-based tokens with 6 or 18 decimals)
    // Most ERC-20 tokens use 6 decimals (USDT, USDC) or 18 decimals (DAI)
    const decimals = ticker === 'usdt' || ticker === 'usdc' ? 6 : 18;
    const amountInSmallestUnit = Math.round(parseFloat(amount) * Math.pow(10, decimals)).toString();
    
    return `ethereum:${contractAddress}@${chainId}/transfer?address=${address}&uint256=${amountInSmallestUnit}`;
  }
  
  // Map common tickers to their URI schemes
  const schemeMap = {
    btc: 'bitcoin',
    eth: 'ethereum',
    ltc: 'litecoin',
    doge: 'dogecoin',
    bch: 'bitcoincash',
    xrp: 'ripple',
    trx: 'tron',
  };
  
  const scheme = schemeMap[ticker] || ticker;
  
  // Ethereum uses 'value' parameter (in wei), others use 'amount'
  if (scheme === 'ethereum') {
    // Convert ETH amount to wei (18 decimals)
    const amountInWei = Math.round(parseFloat(amount) * 1e18).toString();
    return `${scheme}:${address}?value=${amountInWei}`;
  }
  
  return `${scheme}:${address}?amount=${amount}`;
};
import { 
  Copy, 
  CheckCircle2, 
  Clock, 
  RefreshCw, 
  ArrowLeft,
  ExternalLink,
  AlertTriangle,
  Wallet,
  ChevronRight,
  Loader2,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useTransaction } from '../hooks/useExchange';
import { useWebSocket } from '../context/WebSocketContext.jsx';
import { useState, useEffect } from 'react';
import CurrencyIcon from '../components/CurrencyIcon';
import './TransactionStatusPage.css';

const STATUS_CONFIG = {
  waiting: {
    icon: Clock,
    title: 'Waiting for Deposit',
    description: 'Send the exact amount to the address below to start the exchange',
    color: 'warning',
    progress: 10,
  },
  confirming: {
    icon: RefreshCw,
    title: 'Confirming Deposit',
    description: 'Your deposit is being confirmed on the blockchain',
    color: 'info',
    progress: 30,
  },
  exchanging: {
    icon: RefreshCw,
    title: 'Exchanging',
    description: 'Your funds are being exchanged at the best available rate',
    color: 'primary',
    progress: 60,
  },
  sending: {
    icon: Wallet,
    title: 'Sending Funds',
    description: 'Your exchanged funds are being sent to your address',
    color: 'primary',
    progress: 80,
  },
  completed: {
    icon: CheckCircle2,
    title: 'Exchange Complete',
    description: 'Your exchange has been completed successfully',
    color: 'success',
    progress: 100,
  },
  finished: {
    icon: CheckCircle2,
    title: 'Exchange Complete',
    description: 'Your exchange has been completed successfully',
    color: 'success',
    progress: 100,
  },
  failed: {
    icon: AlertTriangle,
    title: 'Exchange Failed',
    description: 'Something went wrong. Please contact support for assistance.',
    color: 'error',
    progress: 0,
  },
  expired: {
    icon: AlertTriangle,
    title: 'Exchange Expired',
    description: 'The exchange time has expired. Please start a new exchange.',
    color: 'error',
    progress: 0,
  },
  refunded: {
    icon: RefreshCw,
    title: 'Refunded',
    description: 'Your funds have been refunded to your address',
    color: 'warning',
    progress: 100,
  },
};

// Countdown Timer Component
const CountdownTimer = ({ expiresAt, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({ minutes: 15, seconds: 0, total: 900 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        onExpire?.();
        return { minutes: 0, seconds: 0, total: 0 };
      }

      const totalSeconds = Math.floor(difference / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      return { minutes, seconds, total: totalSeconds };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining.total === 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, onExpire]);

  if (isExpired) {
    return (
      <div className="countdown expired">
        <AlertTriangle />
        <span>Time Expired</span>
      </div>
    );
  }

  return (
    <div className="countdown">
      <Clock />
      <div className="countdown-display">
        <span className="countdown-label">Time Remaining</span>
        <span className="countdown-time">
          {String(timeLeft.minutes).padStart(2, '0')}:
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
      <div className="countdown-bar">
        <div 
          className="countdown-fill"
          style={{ width: `${(timeLeft.total / 900) * 100}%` }}
        />
      </div>
    </div>
  );
};

const TransactionStatusPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const { data: transaction, isLoading, dataUpdatedAt, refetch } = useTransaction(id);
  const { connected, lastMessage } = useWebSocket();

  // Log status changes for debugging
  useEffect(() => {
    if (transaction) {
      console.log('Status page - Transaction status:', transaction.status, 'Updated at:', new Date(dataUpdatedAt).toLocaleTimeString());
    }
  }, [transaction?.status, dataUpdatedAt]);

  // Listen for WebSocket updates and force refetch
  useEffect(() => {
    if (lastMessage?.type === 'transaction:updated' && lastMessage.transaction?.id === id) {
      console.log('Status page - WebSocket update received:', lastMessage.transaction.status);
      // Force immediate refetch to get full transaction data
      refetch({ cancelRefetch: true });
    }
  }, [lastMessage, id, refetch]);

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleExpire = () => {
    setIsExpired(true);
  };

  if (isLoading || !transaction) {
    return (
      <div className="tx-page">
        <div className="tx-loading">
          <Loader2 className="loading-spinner-large" />
          <p>Loading transaction...</p>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.waiting;
  const StatusIcon = statusConfig.icon;
  const isComplete = transaction.status === 'completed' || transaction.status === 'finished';
  const isFailed = transaction.status === 'failed' || transaction.status === 'expired' || transaction.status === 'refunded' || isExpired;

  return (
    <div className="tx-page">
      <div className="tx-container">
        <div className="tx-header">
          <Link to="/" className="back-link">
            <ArrowLeft />
            <span>New Exchange</span>
          </Link>
          <div className={`live-indicator ${connected ? 'connected' : ''}`}>
            {connected ? (
              <>
                <Wifi size={14} />
                <span className="live-dot"></span>
                <span>Live Updates</span>
              </>
            ) : (
              <>
                <WifiOff size={14} />
                <span>Polling</span>
              </>
            )}
          </div>
          <div className="tx-id-badge">
            <span className="label">Exchange ID:</span>
            <span className="value">{id}</span>
            <button
              className="copy-btn"
              onClick={() => handleCopy(id, 'id')}
            >
              {copiedField === 'id' ? <CheckCircle2 className="copied" /> : <Copy />}
            </button>
          </div>
        </div>

        <div className={`status-card status-${statusConfig.color}`}>
          <div className="status-icon-wrapper">
            <StatusIcon className={`status-icon ${['confirming', 'exchanging', 'sending'].includes(transaction.status) ? 'spinning' : ''}`} />
          </div>
          <h1 className="status-title">{statusConfig.title}</h1>
          <p className="status-description">{statusConfig.description}</p>
        </div>

        {/* Progress */}
        <div className="progress-section">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${statusConfig.progress}%` }}
            />
          </div>
          <div className="progress-steps">
            {['Deposit', 'Confirm', 'Exchange', 'Send'].map((step, index) => {
              const stepProgress = (index + 1) * 25;
              const isActive = statusConfig.progress >= stepProgress - 12.5;
              const isCompleted = statusConfig.progress >= stepProgress;
              
              return (
                <div 
                  key={step} 
                  className={`progress-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                >
                  <div className="step-dot" />
                  <span className="step-label">{step}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="tx-content">
          {/* Deposit Section */}
          {(transaction.status === 'waiting' || transaction.status === 'confirming') && (
            <div className="deposit-section">
              <h3 className="section-title">Send Your Deposit</h3>
              
              {/* Countdown Timer */}
              {transaction.expiresAt && (
                <CountdownTimer 
                  expiresAt={transaction.expiresAt} 
                  onExpire={handleExpire}
                />
              )}
              
              <div className="qr-container">
                <QRCodeSVG 
                  value={getCryptoURI(
                    transaction.fromCurrency,
                    transaction.depositAddress,
                    transaction.fromAmount,
                    transaction.contractAddress,
                    transaction.chainId
                  )}
                  size={180}
                  level="M"
                  bgColor="transparent"
                  fgColor="#3b82f6"
                />
              </div>

              <div className="address-box">
                <div className="address-field">
                  <label>Deposit Address</label>
                  <div className="address-row">
                    <input 
                      type="text" 
                      value={transaction.depositAddress}
                      readOnly
                      className="address-input"
                    />
                    <button
                      className="copy-btn-lg"
                      onClick={() => handleCopy(transaction.depositAddress, 'address')}
                    >
                      {copiedField === 'address' ? (
                        <><CheckCircle2 /> Copied</>
                      ) : (
                        <><Copy /> Copy</>
                      )}
                    </button>
                  </div>
                </div>

                {transaction.depositExtraId && (
                  <div className="address-field">
                    <label>{transaction.fromCurrency.extraIdName || 'Memo/Tag'}</label>
                    <div className="address-row">
                      <input 
                        type="text" 
                        value={transaction.depositExtraId}
                        readOnly
                        className="address-input"
                      />
                      <button
                        className="copy-btn-lg"
                        onClick={() => handleCopy(transaction.depositExtraId, 'extraId')}
                      >
                        {copiedField === 'extraId' ? (
                          <><CheckCircle2 /> Copied</>
                        ) : (
                          <><Copy /> Copy</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="amount-box">
                <div className="amount-display">
                  <span className="amount-label">Amount to send:</span>
                  <span className="amount-value">
                    {transaction.fromAmount} {transaction.fromCurrency.toUpperCase()}
                  </span>
                </div>
                <div className="amount-warning">
                  <AlertTriangle />
                  <span>Send only {transaction.fromCurrency.toUpperCase()} to this address</span>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Details */}
          <div className="details-section">
            <h3 className="section-title">Exchange Details</h3>
            
            <div className="exchange-flow">
              <div className="flow-item">
                <CurrencyIcon 
                  currency={{ 
                    id: transaction.fromCurrencyId || transaction.fromCurrency.toLowerCase(), 
                    ticker: transaction.fromCurrency.toLowerCase(),
                    name: transaction.fromCurrency
                  }}
                  size="lg"
                  className="flow-icon"
                />
                <div className="flow-info">
                  <span className="flow-amount">{transaction.fromAmount}</span>
                  <span className="flow-currency">{transaction.fromCurrency.toUpperCase()}</span>
                </div>
              </div>

              <ChevronRight className="flow-arrow" />

              <div className="flow-item">
                <CurrencyIcon 
                  currency={{ 
                    id: transaction.toCurrencyId || transaction.toCurrency.toLowerCase(), 
                    ticker: transaction.toCurrency.toLowerCase(),
                    name: transaction.toCurrency
                  }}
                  size="lg"
                  className="flow-icon"
                />
                <div className="flow-info">
                  <span className="flow-amount">{transaction.toAmount}</span>
                  <span className="flow-currency">{transaction.toCurrency.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-label">Recipient Address</span>
              <span className="detail-value mono">{transaction.recipientAddress}</span>
            </div>

            {transaction.payinHash && (
              <div className="detail-row">
                <span className="detail-label">Deposit Transaction</span>
                <a 
                  href={`https://explorer.example.com/tx/${transaction.payinHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-link"
                >
                  {transaction.payinHash.slice(0, 24)}...
                  <ExternalLink />
                </a>
              </div>
            )}

            {transaction.payoutHash && (
              <div className="detail-row">
                <span className="detail-label">Payout Transaction</span>
                <a 
                  href={`https://explorer.example.com/tx/${transaction.payoutHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-link"
                >
                  {transaction.payoutHash.slice(0, 24)}...
                  <ExternalLink />
                </a>
              </div>
            )}
          </div>

          {/* Confirmations */}
          {transaction.status === 'confirming' && (
            <div className="confirmations-section">
              <div className="confirmation-bar">
                <div 
                  className="confirmation-fill"
                  style={{ 
                    width: `${(transaction.payinConfirmations / transaction.payinRequiredConfirmations) * 100}%` 
                  }}
                />
              </div>
              <p className="confirmation-text">
                Confirmations: {transaction.payinConfirmations} / {transaction.payinRequiredConfirmations}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="tx-actions">
          {isComplete ? (
            <Link to="/" className="action-btn primary">
              Start New Exchange
            </Link>
          ) : isFailed ? (
            <>
              <a href="/contact" className="action-btn primary">Contact Support</a>
              <Link to="/" className="action-btn secondary">Start New Exchange</Link>
            </>
          ) : (
            <Link to="/" className="action-btn secondary">Start Another Exchange</Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionStatusPage;
